import axios from 'axios'
import React, { ReactElement, ReactNode, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  BaseRequestHeaders,
  ConfigContext,
  ConfigContextState,
} from 'src/context/ConfigContext/ConfigContextBase'
import { ExtendLocales } from 'src/enums'
import { ConfigResponse } from 'src/models/config.models'
import { ConfigState as AppConfigState } from 'src/redux/reducers/data/config.reducer'
import {
  selectIsCspAuthenticatedFromState,
  selectNullSafeLocaleFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'

export {
  ConfigContext,
  defaultConfigContextState,
} from 'src/context/ConfigContext/ConfigContextBase'
export type {
  BaseRequestHeaders,
  ConfigContextActions,
  ConfigContextState,
  ConfigContextValue,
} from 'src/context/ConfigContext/ConfigContextBase'

type ConfigContextProviderProps = {
  initialState: ConfigContextState
  children: ReactNode
}

export default function ConfigContextProvider(
  props: ConfigContextProviderProps,
): ReactElement {
  const { children, initialState } = props
  const locationFromState = useSelector(selectLocationFromState)
  const cspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  // Locale's home is ConfigContext: the localization layer (useTranslate,
  // translateNode) reads it from here, NOT from Redux. This selector is the
  // one remaining bridge from the legacy Redux session state — when user and
  // session state move to React Query, swap this derivation and the
  // localization layer never changes.
  const derivedLocale = useSelector(selectNullSafeLocaleFromState)
  const [configContextState, setConfigContextState] =
    useState<ConfigContextState>(initialState)
  const [didLoad, setDidLoad] = useState<boolean>(false)

  const getBaseRequestHeaders = (): BaseRequestHeaders => {
    return {
      headers: {
        'X-Extend-Brand': configContextState.brand.BRAND_ID,
        'X-Extend-App-ID': configContextState.brand.PRIMARY_DOMAIN,
        'X-Extend-Platform':
          cspAuthenticated || locationFromState.pathname.includes('/csp/')
            ? 'mira'
            : 'web',
        'X-Extend-Platform-Version':
          typeof navigator !== 'undefined' ? navigator?.userAgent || '' : '',
        Accept: 'application/vnd.paywithextend.v2021-03-12+json',
        'Content-Type': 'application/json',
      },
      baseURL:
        configContextState.environment.API_HOST ||
        'https://api-stage.paywithextend.com',
    }
  }

  const refreshConfigContext = async (): Promise<void> => {
    const baseHeaders = getBaseRequestHeaders()
    const requestConfig = {
      ...baseHeaders,
      headers: {
        ...baseHeaders.headers,
        'Content-Type': 'multipart/form-data',
      },
      baseURL: baseHeaders.baseURL,
    }
    const response = await axios.get<ConfigResponse>(
      `/config?${new URLSearchParams({ locale: configContextState.locale }).toString()}`,
      requestConfig,
    )

    const config: AppConfigState = {
      ...response.data,
    }
    setConfigContextState({
      ...configContextState,
      appConfig: config,
    })
  }

  useEffect(() => {
    if (didLoad === false) {
      refreshConfigContext()
      setDidLoad(true)
    }
  }, [didLoad])

  const setLocale = (locale: ExtendLocales) => {
    setConfigContextState({
      ...configContextState,
      locale,
    })
  }

  return (
    <ConfigContext.Provider
      value={{
        ...configContextState,
        locale: derivedLocale ?? configContextState.locale,
        refreshConfigContext,
        setLocale,
        getBaseRequestHeaders,
      }}
    >
      {children}
    </ConfigContext.Provider>
  )
}
